/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { Community } from "@packages/sdk";
import { getCommunityMemberCount } from "@packages/sdk";
import { constantSemanticColors, spacing, Text } from "@packages/ui";

import { CommunityInviteQrCode } from "./CommunityInviteQrCode";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  paddingBottom: spacing.l,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  paddingTop: spacing.l,
};
const brandTextStyle: Interpolation = {
  color: constantSemanticColors.fixedLightenHigh,
  textAlign: "center",
};
const textContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
  maxWidth: spacing.full,
};
const titleStyle: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  textAlign: "center",
};
const detailStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  textAlign: "center",
};

export type CommunityInviteContentProps = {
  community: Community;
  inviteUrl: string;
};

/**
 * Card contents for the community invite `ShareModal`: a small "Hallow" text
 * label at the top, an invite QR code with the community image in its centre, plus the
 * community name / location / member count. The modal opts out of its built-in
 * footer wordmark (`showFooterLogo={false}`) so the branding sits at the top per
 * the Figma. The Download action lives in the modal's share-options row (see
 * `useCommunityInviteAction`), not here.
 */
export const CommunityInviteContent = ({
  community,
  inviteUrl,
}: CommunityInviteContentProps) => {
  const t = useTranslations();

  return (
    <div css={containerStyle}>
      <Text type="title" size="xs" css={brandTextStyle}>
        Hallow
      </Text>
      <CommunityInviteQrCode
        inviteUrl={inviteUrl}
        imageUrl={community.image_url}
        imageAlt={community.name}
      />
      <div css={textContainerStyle}>
        <Text
          overflow="ellipsis"
          type="title"
          size="l"
          css={titleStyle}
          title={community.name}
        >
          {community.name}
        </Text>
        {community.simple_address_label && (
          <Text type="detail" size="xl" css={detailStyle}>
            {community.simple_address_label}
          </Text>
        )}
        <Text type="detail" size="xl" css={detailStyle}>
          {t("plural_members", {
            plural_members: getCommunityMemberCount({ community }),
          })}
        </Text>
      </div>
    </div>
  );
};

CommunityInviteContent.displayName = "CommunityInviteContent";
